import NextImage from "next/image";
import router from "next/router";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { EVariants } from "src/constants/variants";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { colors } from "src/theme";
import { Headline } from "src/theme/typography";
import { Button } from "../../button";
import { FlexRow } from "../../common";
import Logo from "../../Logo";
import { countries, CountryCode } from "../../phoneInput/countries";
import { FlagContainer } from "../../phoneInput/PhoneInput.styles";
import {
  CardButton,
  CardGrid,
  content,
  ContentContainer,
  CountryCardContainer,
  GradientBlock,
  overlay,
} from "./petorvet.styles";

const getLocation: () => CountryCode = () => {
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();

  switch (timeZone) {
    case "Europe/London":
    case "Europe/Belfast":
      return "UK";
    case "Europe/Dublin":
      return "IE";
    default:
      if (["en-CA", "fr-CA"].includes(locale)) {
        return "CA";
      }
      return "US";
  }
};

const initialLocation: CountryCode = getLocation();

export const PetOrVetModal = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [visitorType, setVisitorType] = useState<undefined | "pet owner" | "vet">();
  const [location, setLocation] = useState<"CA" | "IE" | "UK" | "US">(initialLocation);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visitorType) {
      setError(null);
    }
  }, [visitorType]);

  const handleSubmit = () => {
    amplitudeEvent(formatEventName("home", "pet_or_vet_modal", "confirm"));

    let tld = ".com";

    switch (location) {
      case "CA":
        tld = ".ca";
        break;
      case "IE":
        tld = ".ie";
        break;
      case "UK":
        tld = ".co.uk";
        break;
    }

    if (!visitorType) {
      setError("Please let us know if you're a veterinary professional or a pet owner");
      return;
    }

    const directoryName = visitorType === "pet owner" ? "/" : "/clinics";

    localStorage.setItem("pa-visitorType", visitorType);

    if (process.env.NODE_ENV === "production") {
      router.push(`https://www.petsapp${tld}${directoryName}`);
    } else {
      router.push(directoryName);
    }

    setIsOpen(false);
  };

  return (
    <Modal isOpen={isOpen} style={{ content, overlay }}>
      <ContentContainer>
        <FlexRow style={{ marginBottom: 24 }}>
          <Logo />
        </FlexRow>
        <Headline>I am a</Headline>
        <CardGrid>
          <CardButton
            selected={visitorType === "vet"}
            onClick={() => {
              setVisitorType("vet");
              amplitudeEvent(formatEventName("home", "pet_or_vet_modal", "vet"));
            }}
          >
            <NextImage
              width={200}
              height={200}
              src="/images/vetorpet/labrador-with-stethoscope.png"
            />
            <Headline>Veterinary professional</Headline>
          </CardButton>
          <CardButton
            selected={visitorType === "pet owner"}
            onClick={() => {
              setVisitorType("pet owner");
              amplitudeEvent(formatEventName("home", "pet_or_vet_modal", "pet_owner"));
            }}
          >
            <NextImage width={200} height={200} src="/images/vetorpet/yorkie-and-hamster.png" />
            <Headline>Pet owner</Headline>
          </CardButton>
        </CardGrid>
        {error && <Headline textColor={colors.redDark}>{error}</Headline>}
        <Headline>in</Headline>
        <CountryCardContainer>
          {countries.map(({ flag, countryCode, name }) => (
            <CardButton
              selected={location === countryCode}
              flexDirection="row"
              key={countryCode}
              onClick={() => {
                setLocation(countryCode);
                amplitudeEvent(
                  formatEventName("home", "pet_or_vet_modal", `${countryCode}_button`),
                );
              }}
            >
              <FlagContainer>
                <NextImage width={24} height={24} src={flag} />
              </FlagContainer>
              <Headline>{name}</Headline>
            </CardButton>
          ))}
        </CountryCardContainer>
        <Button variant={EVariants.TERTIARY} onClick={handleSubmit}>
          Confirm
        </Button>
      </ContentContainer>
      <GradientBlock />
    </Modal>
  );
};
